"use client";

import { useEffect } from "react";

// =====================================================================
// Interacciones de la landing v3.1 (porteo de landing.js + helpers de
// motion.js). Sin dependencias de animación: vanilla DOM dentro de
// #lpRoot. Progressive enhancement: el contenido es visible por defecto;
// este componente agrega `.anim` a la raíz `.lp` solo si hay animación
// (no reduced-motion), y `.no-anim` como fail-safe si el contexto no
// pinta. Si este script no corre, NADA queda invisible.
//
// Mantiene el contrato de marca: una sola cosa en movimiento por
// viewport, loops que se pausan fuera de viewport/pestaña oculta,
// respeto de prefers-reduced-motion.
// =====================================================================

type IaAnswer = { me: string; ai: string; chip: string };

const IA_ANSWERS: Record<string, IaAnswer> = {
  agenda: {
    me: "Agendame a Juan el martes a las 10",
    ai: "Listo. Agendé a <b>Juan Pérez</b> el martes 10:00, sesión de 50 min. ¿Te aviso 24 h antes?",
    chip: "Turno creado · mar 10:00",
  },
  factura: {
    me: "¿Cuánto facturé este mes?",
    ai: "Este mes cobraste <b>$ 318.400</b> en 47 sesiones. Te quedan <b>3 pagos pendientes</b> por $ 21.000.",
    chip: "Resumen de junio",
  },
  cobro: {
    me: "Cobrale la sesión de hoy a Sofía",
    ai: "Registré el cobro de <b>$ 8.000</b> a <b>Sofía R.</b> por la sesión de hoy. Saldo de Sofía: al día.",
    chip: "Cobro registrado · $ 8.000",
  },
  disponibles: {
    me: "¿Qué turnos tengo disponibles la semana próxima?",
    ai: "La semana próxima tenés <b>9 huecos libres</b>: martes 11:00 y 16:00, miércoles 9:00, jueves 10:00, 15:00 y 17:00, y viernes 12:00. ¿Compartís tu link de reservas?",
    chip: "9 turnos disponibles",
  },
};

const DONE_CHIP_SVG =
  '<svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M3 7l3 3 5-6" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>';

export function LandingMotion() {
  useEffect(() => {
    const root = document.getElementById("lpRoot");
    if (!root) return;

    const RM =
      typeof matchMedia === "function" &&
      matchMedia("(prefers-reduced-motion: reduce)").matches;

    const timers: ReturnType<typeof setTimeout>[] = [];
    const cleanups: Array<() => void> = [];
    const after = (ms: number, fn: () => void) => {
      const id = setTimeout(fn, RM ? Math.min(ms, 50) : ms);
      timers.push(id);
      return id;
    };
    // timed([[ms, fn]...]) → cancelador (para loops que se reinician)
    const timed = (steps: Array<[number, () => void]>) => {
      const ids = steps.map(([ms, fn]) =>
        setTimeout(fn, RM ? Math.min(ms, 50) : ms),
      );
      ids.forEach((id) => timers.push(id));
      return () => ids.forEach(clearTimeout);
    };
    const countUp = (el: HTMLElement, to: number, dur: number) => {
      if (RM || dur <= 0) {
        el.textContent = to.toLocaleString("es-AR");
        return;
      }
      const start = Date.now();
      const tick = () => {
        let k = Math.min(1, (Date.now() - start) / dur);
        k = 1 - Math.pow(1 - k, 3);
        el.textContent = Math.round(to * k).toLocaleString("es-AR");
        if (k < 1) after(40, tick);
      };
      tick();
    };

    const $ = <T extends Element>(sel: string, r: ParentNode = root) =>
      r.querySelector(sel) as T | null;
    const $$ = <T extends Element>(sel: string, r: ParentNode = root) =>
      Array.from(r.querySelectorAll(sel)) as T[];

    const inView = (el: Element, margin = 60) => {
      const rct = el.getBoundingClientRect();
      if (rct.height === 0 && rct.width === 0) return false;
      return rct.top < window.innerHeight - margin && rct.bottom > 0;
    };
    const throttle = (fn: () => void) => {
      let t: ReturnType<typeof setTimeout> | null = null;
      return () => {
        if (t) return;
        t = setTimeout(() => {
          t = null;
          fn();
        }, 90);
      };
    };

    // Activa el modo animado (sin él, [data-reveal]/[data-seq] quedan visibles).
    if (!RM) root.classList.add("anim");

    /* ---- nav scroll state ---- */
    const nav = $("#nav");
    const onScroll = () => {
      if (nav) nav.classList.toggle("scrolled", window.scrollY > 16);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    cleanups.push(() => window.removeEventListener("scroll", onScroll));

    /* ---- mobile menu (hamburguesa) ---- */
    const burger = $<HTMLButtonElement>("#navBurger");
    const mobile = $<HTMLElement>("#navMobile");
    if (burger && mobile) {
      const toggle = () => {
        const open = mobile.classList.toggle("open");
        burger.setAttribute("aria-expanded", open ? "true" : "false");
      };
      const close = () => {
        mobile.classList.remove("open");
        burger.setAttribute("aria-expanded", "false");
      };
      burger.addEventListener("click", toggle);
      mobile.querySelectorAll("a").forEach((a) => a.addEventListener("click", close));
      cleanups.push(() => burger.removeEventListener("click", toggle));
    }

    /* ---- reveals + sequences (chequeo de viewport propio) ---- */
    const reveals = $$<HTMLElement & { _seen?: boolean }>("[data-reveal]");
    const seqRoots = $$<HTMLElement & { _played?: boolean }>("[data-seqroot]");
    const iaState = { root: null as HTMLElement | null, played: false, play: () => {} };

    const fireReveal = (el: HTMLElement & { _seen?: boolean }) => {
      if (el._seen) return;
      el._seen = true;
      el.classList.add("seen");
    };
    const fireSeq = (rt: HTMLElement & { _played?: boolean }) => {
      if (rt._played) return;
      rt._played = true;
      const items = $$<HTMLElement>("[data-seq]", rt);
      const gap = parseInt(rt.dataset.seqgap ?? "", 10) || 280;
      items.forEach((el, i) =>
        after(RM ? 0 : i * gap + 180, () => el.classList.add("on")),
      );
      const ctr = rt.querySelector<HTMLElement>("[data-countup]");
      if (ctr)
        after(RM ? 0 : items.length * gap + 120, () =>
          countUp(ctr, parseInt(ctr.dataset.countup ?? "0", 10), RM ? 0 : 1100),
        );
    };
    const scan = () => {
      reveals.forEach((el) => {
        if (!el._seen && inView(el, 40)) fireReveal(el);
      });
      seqRoots.forEach((el) => {
        if (!el._played && inView(el, 90)) fireSeq(el);
      });
      if (iaState.root && !iaState.played && inView(iaState.root, 120)) {
        iaState.played = true;
        iaState.play();
      }
    };
    if (RM) {
      reveals.forEach(fireReveal);
      seqRoots.forEach(fireSeq);
    }
    scan();
    const scanThrottled = throttle(scan);
    window.addEventListener("scroll", scanThrottled, { passive: true });
    window.addEventListener("resize", scanThrottled);
    cleanups.push(() => {
      window.removeEventListener("scroll", scanThrottled);
      window.removeEventListener("resize", scanThrottled);
    });
    [200, 700, 1500].forEach((t) => after(t, scan));
    // catch-all: nunca dejar contenido invisible
    after(4500, () => {
      reveals.forEach(fireReveal);
      seqRoots.forEach(fireSeq);
      if (iaState.root && !iaState.played) {
        iaState.played = true;
        iaState.play();
      }
    });

    // Detección de contexto sin pintado → mostrar todo sin animar.
    let painting = false;
    requestAnimationFrame(() => {
      painting = true;
    });
    after(560, () => {
      if (painting) return;
      root.classList.add("no-anim");
      reveals.forEach(fireReveal);
      seqRoots.forEach(fireSeq);
      if (iaState.root && !iaState.played) {
        iaState.played = true;
        iaState.play();
      }
      const t = $("#hTyping") as HTMLElement | null;
      if (t) t.style.display = "none";
    });

    /* ---- HERO — demo viva del asistente ---- */
    const stage = $("#heroDemo");
    if (stage) {
      const cmd = $("#hCmd"),
        typ = $("#hTyping"),
        prop = $("#hPropose"),
        okBtn = $("#hConfirm"),
        done = $("#hDone"),
        newSlot = $("#hNewSlot"),
        payPill = $("#hPayPill"),
        toast = $("#hToast");
      const parts = [cmd, typ, prop, done];

      const reset = () => {
        parts.forEach((n) => n && n.classList.remove("in"));
        okBtn && okBtn.classList.remove("pbtn--working");
        newSlot && newSlot.classList.remove("in", "slot--moving");
        if (payPill) {
          payPill.className = "vpill vpill--pend vmono";
          payPill.textContent = "pendiente";
        }
        toast && toast.classList.remove("in");
      };
      const cycle = () =>
        timed([
          [200, () => cmd && cmd.classList.add("in")],
          [1050, () => typ && typ.classList.add("in")],
          [
            2250,
            () => {
              typ && typ.classList.remove("in");
              prop && prop.classList.add("in");
            },
          ],
          [3550, () => okBtn && okBtn.classList.add("pbtn--working")],
          [
            4150,
            () => {
              prop && prop.classList.remove("in");
              newSlot && newSlot.classList.add("in", "slot--moving");
            },
          ],
          [
            4750,
            () => {
              newSlot && newSlot.classList.remove("slot--moving");
              if (payPill) {
                payPill.className = "vpill vpill--ok vmono";
                payPill.textContent = "cobrado";
              }
              done && done.classList.add("in");
              toast && toast.classList.add("in");
            },
          ],
        ]);

      if (RM) {
        cmd && cmd.classList.add("in");
        prop && prop.classList.add("in");
        done && done.classList.add("in");
        newSlot && newSlot.classList.add("in");
        if (payPill) {
          payPill.className = "vpill vpill--ok vmono";
          payPill.textContent = "cobrado";
        }
        toast && toast.classList.add("in");
      } else {
        let paused = false;
        let cancel: (() => void) | undefined;
        let loop: ReturnType<typeof setTimeout> | undefined;
        const run = () => {
          if (paused) return;
          reset();
          cancel = cycle();
          loop = setTimeout(run, 6800);
          timers.push(loop);
        };
        run();
        const onVis = () => {
          if (document.hidden) {
            paused = true;
            cancel && cancel();
            loop && clearTimeout(loop);
          } else if (paused) {
            paused = false;
            run();
          }
        };
        document.addEventListener("visibilitychange", onVis);
        cleanups.push(() =>
          document.removeEventListener("visibilitychange", onVis),
        );
      }
    }

    /* ---- BLOQUE IA — chips → respuesta del asistente ---- */
    const iaBlock = $("#iaBlock");
    if (iaBlock) {
      const chips = $$<HTMLButtonElement>(".chip-ex", iaBlock);
      const feed = $<HTMLElement>("#iaFeed", iaBlock);
      if (chips.length && feed) {
        let stop: (() => void) | undefined;
        const render = (key: string) => {
          stop && stop();
          const a = IA_ANSWERS[key];
          if (!a) return;
          feed.innerHTML =
            '<div class="bubble bubble--me" id="_iaMe">' +
            a.me +
            '</div><div class="bubble bubble--ai" id="_iaAi"><span class="typing"><span></span><span></span><span></span></span></div>';
          const me = $<HTMLElement>("#_iaMe", feed);
          const ai = $<HTMLElement>("#_iaAi", feed);
          requestAnimationFrame(() => me && me.classList.add("in"));
          stop = timed([
            [120, () => ai && ai.classList.add("in")],
            [
              RM ? 130 : 1150,
              () => {
                if (ai)
                  ai.innerHTML =
                    a.ai +
                    '<div class="done-chip in" style="margin-top:9px">' +
                    DONE_CHIP_SVG +
                    a.chip +
                    "</div>";
              },
            ],
          ]);
        };
        const select = (chip: HTMLButtonElement) => {
          chips.forEach((c) =>
            c.setAttribute("aria-selected", c === chip ? "true" : "false"),
          );
          render(chip.dataset.key ?? "agenda");
        };
        chips.forEach((c) =>
          c.addEventListener("click", () => select(c)),
        );
        iaState.root = iaBlock as HTMLElement;
        iaState.play = () => select(chips[0]);
        after(2200, () => {
          if (!iaState.played) {
            iaState.played = true;
            iaState.play();
          }
        });
      }
    }

    return () => {
      timers.forEach(clearTimeout);
      cleanups.forEach((fn) => fn());
    };
  }, []);

  return null;
}
